import { onMounted, onBeforeUnmount, ref } from "vue";
import { useRoute } from "vue-router";
import { userAnalyticsService } from "@/services";
import { SHIPMENTS_ANALYTICS_EVENTS } from "@/features/shipments/constants/analytics";

/**
 * Composable to track the time a user spends on a page.
 * Fires a "Time Spent" event when the component unmounts.
 *
 * @param pageName - The name of the page (e.g., "Shipments", "DnD")
 * @param additionalProps - Additional properties to include in the event
 *
 * @example
 * import { usePageTimeTracking } from "@/compositions/usePageTimeTracking";
 *
 * export default {
 *   setup() {
 *     usePageTimeTracking("Shipments", { tab: route.query.tab });
 *   }
 * };
 */
export const usePageTimeTracking = (pageName: string, additionalProps?: Record<string, string | number | boolean>) => {
  const route = useRoute();
  const startTime = ref<number>(0);

  onMounted(() => {
    startTime.value = Date.now();
  });

  onBeforeUnmount(() => {
    const endTime = Date.now();
    const durationSeconds = Math.round((endTime - startTime.value) / 1000);

    // Build event props
    const eventProps: Record<string, string | number | boolean> = {
      page: pageName,
      seconds: durationSeconds,
      ...additionalProps
    };

    // Add current tab if available
    if (route.query.tab) {
      eventProps.tab = String(route.query.tab);
    }

    // Use constants for shipments, fallback to dynamic event name for other pages
    const eventName = pageName === "Shipments" ? SHIPMENTS_ANALYTICS_EVENTS.TIME_SPENT : `${pageName}: Time Spent`;

    userAnalyticsService.trackEvent(eventName, eventProps);
  });
};
